import { useState } from 'react';
import axios from 'axios';
import { Users, Target, TrendingUp, MapPin, Award, Save, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CROWD_QUALITY_OPTIONS = [
  { value: 'collector-focused', label: 'Collector-Focused', desc: 'Serious buyers looking for specific items' },
  { value: 'casual', label: 'Casual Fans', desc: 'Browse more than buy' },
  { value: 'mixed', label: 'Mixed', desc: 'Good variety of buyers and browsers' },
];

const CROWD_DEMOGRAPHICS_OPTIONS = [
  { value: 'anime-heavy', label: 'Anime Heavy' },
  { value: 'comic-heavy', label: 'Comic Heavy' },
  { value: 'family-heavy', label: 'Family Heavy' },
  { value: 'college-heavy', label: 'College Heavy' },
  { value: 'mixed', label: 'Mixed Demographics' },
];

const PRICE_COMPETITION_OPTIONS = [
  { value: 'low', label: 'Low', desc: 'Few similar vendors, good pricing power' },
  { value: 'medium', label: 'Medium', desc: 'Some competition, standard pricing' },
  { value: 'high', label: 'High', desc: 'Many competitors, aggressive pricing needed' },
];

const RatingBar = ({ value, max = 10, label }) => {
  const percentage = ((value || 0) / max) * 100;
  const color = percentage >= 70 ? 'bg-green-500' : percentage >= 40 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-bold">{value || '-'}/{max}</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

export default function ShowQualityMetrics({ event, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    estimated_attendance: event.estimated_attendance || '',
    buyer_intent_rating: event.buyer_intent_rating || '',
    foot_traffic_rating: event.foot_traffic_rating || '',
    crowd_quality: event.crowd_quality || '',
    crowd_demographics: event.crowd_demographics || '',
    competitor_count: event.competitor_count || '',
    similar_booth_count: event.similar_booth_count || '',
    price_competition_level: event.price_competition_level || '',
    dead_zones_notes: event.dead_zones_notes || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(`${BACKEND_URL}/api/events/${event.id}`, {
        ...event,
        estimated_attendance: formData.estimated_attendance ? parseInt(formData.estimated_attendance) : null,
        buyer_intent_rating: formData.buyer_intent_rating ? parseInt(formData.buyer_intent_rating) : null,
        foot_traffic_rating: formData.foot_traffic_rating ? parseInt(formData.foot_traffic_rating) : null,
        crowd_quality: formData.crowd_quality || null,
        crowd_demographics: formData.crowd_demographics || null,
        competitor_count: formData.competitor_count ? parseInt(formData.competitor_count) : null,
        similar_booth_count: formData.similar_booth_count ? parseInt(formData.similar_booth_count) : null,
        price_competition_level: formData.price_competition_level || null,
        dead_zones_notes: formData.dead_zones_notes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Show quality metrics saved');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to save metrics');
    } finally {
      setSaving(false);
    }
  };

  const hasData = event.estimated_attendance || event.buyer_intent_rating || event.crowd_quality;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Show Quality Metrics
          </h3>
          <p className="text-sm text-muted-foreground">Track crowd quality, competition, and buyer intent</p>
        </div>
        <Button 
          variant={isEditing ? "default" : "outline"} 
          size="sm"
          onClick={() => isEditing ? handleSave() : setIsEditing(true)}
          disabled={saving}
        >
          {isEditing ? (
            <>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </>
          ) : 'Edit Metrics'}
        </Button>
      </div>

      {!hasData && !isEditing ? (
        <Card className="bg-card border-border/50 p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">No show quality data recorded yet</p>
          <Button onClick={() => setIsEditing(true)}>Add Metrics</Button>
        </Card>
      ) : isEditing ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Attendance & Traffic */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Attendance & Traffic
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Estimated Attendance</Label>
                <Input
                  type="number"
                  name="estimated_attendance"
                  value={formData.estimated_attendance}
                  onChange={handleChange}
                  placeholder="e.g., 50000"
                  className="bg-secondary/50"
                />
              </div>
              <div>
                <Label>Foot Traffic Rating (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  name="foot_traffic_rating"
                  value={formData.foot_traffic_rating}
                  onChange={handleChange}
                  placeholder="1 = dead, 10 = packed"
                  className="bg-secondary/50"
                />
              </div>
              <div>
                <Label>Buyer Intent Rating (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  name="buyer_intent_rating"
                  value={formData.buyer_intent_rating}
                  onChange={handleChange}
                  placeholder="1 = window shoppers, 10 = came to buy"
                  className="bg-secondary/50"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  &quot;People came to spend&quot; vs &quot;people came to cosplay and leave&quot;
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Crowd Quality */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="h-4 w-4 text-primary" />
                Crowd Quality
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Crowd Vibe</Label>
                <select
                  name="crowd_quality"
                  value={formData.crowd_quality}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-foreground"
                >
                  <option value="">Select crowd vibe...</option>
                  {CROWD_QUALITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Crowd Demographics</Label>
                <select
                  name="crowd_demographics"
                  value={formData.crowd_demographics}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-foreground"
                >
                  <option value="">Select demographics...</option>
                  {CROWD_DEMOGRAPHICS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Competition */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Competition & Saturation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Total Vendors in Your Category</Label>
                <Input
                  type="number"
                  name="competitor_count"
                  value={formData.competitor_count}
                  onChange={handleChange}
                  placeholder="Number of art vendors"
                  className="bg-secondary/50"
                />
              </div>
              <div>
                <Label>Similar Style Booths</Label>
                <Input
                  type="number"
                  name="similar_booth_count"
                  value={formData.similar_booth_count}
                  onChange={handleChange}
                  placeholder="Prints/anime art/cards vendors"
                  className="bg-secondary/50"
                />
              </div>
              <div>
                <Label>Price Competition Level</Label>
                <select
                  name="price_competition_level"
                  value={formData.price_competition_level}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-foreground"
                >
                  <option value="">Select level...</option>
                  {PRICE_COMPETITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Dead Zones */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Booth Location Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label>Dead Zones / Layout Issues</Label>
                <textarea
                  name="dead_zones_notes"
                  value={formData.dead_zones_notes}
                  onChange={handleChange}
                  placeholder="Note any bad layout areas, dead zones, or location issues..."
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-foreground resize-none"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Display Mode - Attendance & Traffic */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Attendance & Traffic
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                <span className="text-sm text-muted-foreground">Estimated Attendance</span>
                <span className="text-xl font-bold">
                  {event.estimated_attendance ? event.estimated_attendance.toLocaleString() : '-'}
                </span>
              </div>
              <RatingBar value={event.foot_traffic_rating} label="Foot Traffic" />
              <RatingBar value={event.buyer_intent_rating} label="Buyer Intent" />
            </CardContent>
          </Card>

          {/* Display Mode - Crowd Quality */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="h-4 w-4 text-primary" />
                Crowd Quality
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                <span className="text-sm text-muted-foreground">Crowd Vibe</span>
                <span className="font-medium capitalize">
                  {event.crowd_quality?.replace('-', ' ') || '-'}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                <span className="text-sm text-muted-foreground">Demographics</span>
                <span className="font-medium capitalize">
                  {event.crowd_demographics?.replace('-', ' ') || '-'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Display Mode - Competition */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Competition
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-2xl font-bold">{event.competitor_count || '-'}</p>
                  <p className="text-xs text-muted-foreground">Art Vendors</p>
                </div>
                <div className="p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-2xl font-bold">{event.similar_booth_count || '-'}</p>
                  <p className="text-xs text-muted-foreground">Similar Style</p>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                <span className="text-sm text-muted-foreground">Price Competition</span>
                <span className={`font-medium capitalize px-2 py-1 rounded ${
                  event.price_competition_level === 'low' ? 'bg-green-500/20 text-green-400' :
                  event.price_competition_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  event.price_competition_level === 'high' ? 'bg-red-500/20 text-red-400' : ''
                }`}>
                  {event.price_competition_level || '-'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Display Mode - Location Notes */}
          {event.dead_zones_notes && (
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  Location Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {event.dead_zones_notes}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
